'use client';

import clsx from 'clsx';
import { DateTime } from 'luxon';
import React, {
  useCallback, useEffect, useId, useMemo, useRef, useState
} from 'react';
import { IoMdClose } from 'react-icons/io';
import { useSearchParams } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'prismjs/components/prism-json';
import 'prismjs/themes/prism.css';
import 'react-toastify/dist/ReactToastify.css';

import APIGateway from '@/api';
import AgentVariables from '@/components/agentVariables/agentVariables';
import { ChatConversationMessage } from '@/components/chatConversation/chatConversation';
import { Loader } from '@/components/loader/loader';
import LogDetail from '@/components/logs/logDetail/logDetail';
import RenderLogList from '@/components/logs/renderLogList/renderLogList';
import PlaygroundChat from '@/components/playgroundChat/playgroundChat';
import { PlaygroundInfo, PlaygroundType } from '@/components/playgroundHeader/playgroundHeader';
import { useAuthContext } from '@/hooks/useAuthContext';
import { useStreamingParser } from '@/hooks/useStreamingParser';
import { AgentInvokeArgs, AgentInvokeStreamChunk } from '@/types/agent';
import {
  AgentInvokeApiReqUser,
  ChatMessageContentStructured,
  ChatMessageContentTextual,
  ChatMsgHistory,
  ChatMsgHistoryItem,
  ChatMsgTypeEnum,
  ChatRoleEnum,
  CompletionResponseTypeEnum
} from '@/types/chat';
import type { PlaygroundLog } from '@/types/logs';

import styles from './page.module.scss';

type Props = {
  an: string;
};

export default function AgentAidp( props: Props ) {
  const { sessionContext } = useAuthContext();
  const id = useId();

  const {
    an: propAgentId
  } = props;

  const [ sAgentId ] = useState<string>( propAgentId || 'unknown-agent' );
  const [ sChatMsgHistory, setSChatMsgHistory ] = useState<ChatMsgHistory>( [] );
  const [ sChatUsers, setSChatUsers ] = useState<AgentInvokeApiReqUser[]>();
  const [ sContext, setSContext ] = useState<string | undefined>();
  const [ sCorrelationId, setSCorrelationId ] = useState<string | undefined>();
  const [ sDebug, _setSDebug ] = useState<boolean>( false );
  const [ sInput, setSInput ] = useState<string>( '' );
  const [ sInputs, setSInputs ] = useState<string | undefined>();
  const [ sIsLoading, setSIsLoading ] = useState<boolean>( true );
  const [ sIsWaiting, setSIsWaiting ] = useState<boolean>( false );

  const [ sLogs, setSLogs ] = useState<PlaygroundLog[]>( [] );
  const [ sActiveLog, setSActiveLog ] = useState<PlaygroundLog | undefined>( undefined );

  const abortControllerRef = useRef<AbortController | null>( null );
  const correlationIdRef = useRef<string | undefined>( undefined );
  const currentMessageIdRef = useRef<string | undefined>( undefined );
  const inputRef = useRef<string>( '' );
  const messagesEndRef = useRef<null | HTMLDivElement>( null );
  /** Guards the replay pre-fill effect so the async fetch only kicks off once. */
  const replayPrefillStartedRef = useRef<boolean>( false );
  /** Tracks whether the auto-send half of a replay has already fired; prevents
   *  dependency changes (e.g. sChatMsgHistory) from triggering a duplicate submit. */
  const replayHandledRef = useRef<boolean>( false );
  /** Mode (text/structured) recovered from the fetched replay payload, so the
   *  auto-send effect can invoke onClickChat with the right argument. */
  const replayModeRef = useRef<'text' | 'structured'>( 'text' );
  /** Flipped true once the async pre-fill fetch completes; this is React state
   *  (not a ref) so the auto-send effect re-runs after the fetch resolves. */
  const [ replayReady, setReplayReady ] = useState<boolean>( false );

  const [ searchParams ] = useSearchParams();

  useEffect( () => {
    setSIsLoading( false );
  }, [ props ] );

  useEffect( () => {
    inputRef.current = sInput;
  }, [ sInput ] );

  // Replay: when the URL has ?replayRequestId=<id>, fetch the original request
  // log from the API, rebuild the chat state from it, and let the auto-send
  // effect resubmit it. Fetching server-side keeps the URL short (vs. shipping
  // history through it, which 431'd) and always pulls the latest data shape.
  //
  // Expected logged payload (meta_data.request):
  //   { messages: [{ author: {id, type}, created_at, updated_at, message_id,
  //                  content: {...} }, ...],
  //     context: { values: [{key, value}, ...] },
  //     users: [{id, display_name, type}] }
  useEffect( () => {
    if ( replayPrefillStartedRef.current ) return;
    const replayRequestId = searchParams.get( 'replayRequestId' );
    if ( !replayRequestId ) return;
    if ( !sessionContext?.userId ) return;
    replayPrefillStartedRef.current = true;

    const currentUserId = sessionContext.userId;
    let cancelled = false;

    ( async() => {
      try {
        const res = await APIGateway.logs.requestLogsGet( {
          agent_id: sAgentId,
          request_id: replayRequestId,
          batch_size: 1
        } );
        if ( cancelled ) return;
        const log = res?.completion_requests?.[ 0 ];
        const requestPayload = log?.meta_data?.request as
          | { messages?: unknown[]; context?: { values?: unknown }; users?: unknown[] }
          | undefined;
        const messages = requestPayload?.messages;
        if ( !Array.isArray( messages ) || messages.length === 0 ) {
          console.warn( 'Replay: no messages in fetched request log', replayRequestId );
          replayHandledRef.current = true; // skip auto-send
          return;
        }

        // Last message becomes the input being resubmitted. Some logs serialize
        // StructuredContent.data as a JSON-encoded string; normalize to an
        // object first so JSON.stringify here + onClickChat's JSON.parse round
        // trip produces a dict on the wire (the backend rejects a string).
        const last = messages[ messages.length - 1 ] as { content?: unknown };
        const content = last?.content;
        let inputText = '';
        let inputMode: 'text' | 'structured' = 'text';
        if ( typeof content === 'string' ) {
          inputText = content;
        } else if ( content && typeof content === 'object' ) {
          const c = content as { type?: string; text?: unknown; data?: unknown };
          if ( c.type === 'TextualContent' && typeof c.text === 'string' ) {
            inputText = c.text;
          } else if ( c.type === 'StructuredContent' && c.data !== undefined ) {
            let dataObj: unknown = c.data;
            if ( typeof dataObj === 'string' ) {
              try { dataObj = JSON.parse( dataObj ); } catch { /* keep raw */ }
            }
            inputText = JSON.stringify( dataObj, null, 2 );
            inputMode = 'structured';
          } else if ( typeof c.text === 'string' ) {
            inputText = c.text;
          } else {
            inputText = JSON.stringify( content, null, 2 );
            inputMode = 'structured';
          }
        }
        inputRef.current = inputText;
        setSInput( inputText );
        replayModeRef.current = inputMode;

        // Build authorId → role map from users[] so messages with no inline role
        // hint can still be classified.
        const userRoleMap = new Map<string, ChatRoleEnum>();
        if ( Array.isArray( requestPayload?.users ) ) {
          for ( const u of requestPayload.users ) {
            const uu = u as { id?: unknown; type?: unknown };
            const id = uu.id;
            const type = typeof uu.type === 'string' ? uu.type.toLowerCase() : '';
            if ( typeof id === 'string' && ( type === 'assistant' || type === 'user' || type === 'developer' ) ) {
              userRoleMap.set( id, type as ChatRoleEnum );
            }
          }
        }

        const determineRole = ( m: any ): ChatRoleEnum => {
          // Priority: explicit role → nested author.type → top-level type
          //         → users[] lookup → authorId/agentId match → USER default
          const candidates: unknown[] = [ m?.role, m?.author?.type, m?.type ];
          for ( const c of candidates ) {
            if ( typeof c === 'string' ) {
              const lc = c.toLowerCase();
              if ( lc === 'assistant' ) return ChatRoleEnum.ASSISTANT;
              if ( lc === 'user' ) return ChatRoleEnum.USER;
              if ( lc === 'developer' ) return ChatRoleEnum.DEVELOPER;
            }
          }
          const aid = typeof m?.author?.id === 'string'
            ? m.author.id
            : ( typeof m?.authorId === 'string' ? m.authorId : undefined );
          if ( aid ) {
            const mapped = userRoleMap.get( aid );
            if ( mapped ) return mapped;
            if ( aid === sAgentId ) return ChatRoleEnum.ASSISTANT;
          }
          return ChatRoleEnum.USER;
        };

        const rebuilt: ChatMsgHistory = messages.slice( 0, -1 ).map( ( m: any ) => {
          const role = determineRole( m );
          const authorId = role === ChatRoleEnum.ASSISTANT ? sAgentId : currentUserId;
          const createdAtStr = m?.created_at ?? m?.createdAt;
          const updatedAtStr = m?.updated_at ?? m?.updatedAt ?? createdAtStr;
          const messageId = ( typeof m?.message_id === 'string' && m.message_id )
            || ( typeof m?.messageId === 'string' && m.messageId )
            || crypto.randomUUID();
          const parsedCreated = createdAtStr ? DateTime.fromISO( String( createdAtStr ) ) : DateTime.now();
          const parsedUpdated = updatedAtStr ? DateTime.fromISO( String( updatedAtStr ) ) : parsedCreated;
          return {
            authorId,
            content: m?.content,
            createdAt: parsedCreated.isValid ? parsedCreated : DateTime.now(),
            updatedAt: parsedUpdated.isValid ? parsedUpdated : DateTime.now(),
            messageId,
            role
          } as ChatMsgHistoryItem;
        } );
        setSChatMsgHistory( rebuilt );

        const ctxValues = requestPayload?.context?.values;
        if ( Array.isArray( ctxValues ) ) {
          // sContext is the JSON string AgentVariables edits; onClickChat
          // JSON.parses it back into context.values when submitting.
          setSContext( JSON.stringify( ctxValues ) );
        }
        setReplayReady( true );
      } catch( e ) {
        console.error( 'Failed to fetch replay request log', e );
        replayHandledRef.current = true; // skip auto-send on fetch failure
      }
    } )();

    return () => { cancelled = true; };
  }, [ sessionContext, sAgentId, searchParams ] );

  useEffect( () => {
    const chatUsers = [
      {
        displayName: 'User',
        id: sessionContext!.userId!,
        type: ChatRoleEnum.USER
      },
      {
        displayName: sAgentId,
        id: sAgentId,
        type: ChatRoleEnum.ASSISTANT
      }
    ];

    setSChatUsers( chatUsers );
  }, [ sAgentId, sessionContext ] );

  const parseAgentChunk = useCallback( ( chunk: unknown ) => {
    const typedChunk = chunk as AgentInvokeStreamChunk;

    // Store correlationId synchronously in ref so it's available when loadLogs runs
    if( typedChunk.correlation_id && !sCorrelationId ) {
      correlationIdRef.current = typedChunk.correlation_id;
      setSCorrelationId( typedChunk.correlation_id );
    }

    // Handle errors
    if( typedChunk.type === CompletionResponseTypeEnum.AI_DATA_PLATFORM_ERROR ) {
      return { error: typedChunk.errorMessage || typedChunk.type, shouldUpdate: false };
    }

    if( typedChunk.type === CompletionResponseTypeEnum.AI_DATA_PLATFORM_FAILURE ) {
      return { error: typedChunk.failureMessage || typedChunk.type, shouldUpdate: false };
    }

    // Process result chunks
    if( typedChunk.type === CompletionResponseTypeEnum.AI_DATA_PLATFORM_RESULT && typedChunk.messages && typedChunk.messages.length > 0 ) {
      const msg = typedChunk.messages[0];

      if( !msg.content ) {
        return { shouldUpdate: false };
      }

      // Use the message ID, if specified, to know if this should replace an existing message
      // or a new one should get created
      currentMessageIdRef.current = msg.message_id || typedChunk.response_id;

      // AIDP sends full accumulated content in each chunk (not deltas)
      // So we use a special marker to indicate this is a full replacement
      // The marker will be stripped in handleUpdateAgentMessage
      return {
        content: '__FULL_REPLACE__' + JSON.stringify( msg.content ),
        shouldUpdate: true
      };
    }

    return { shouldUpdate: false };
  }, [ sCorrelationId ] );

  const handleUpdateAgentMessage = useCallback( ( accumulatedContent: string ) => {
    // AIDP chunks contain full content (not deltas), so extract the latest
    // by taking the last __FULL_REPLACE__ marker
    const fullReplaceMarker = '__FULL_REPLACE__';
    const lastMarkerIndex = accumulatedContent.lastIndexOf( fullReplaceMarker );
    const contentJson = lastMarkerIndex >= 0
      ? accumulatedContent.substring( lastMarkerIndex + fullReplaceMarker.length )
      : accumulatedContent;

    const content = JSON.parse( contentJson );

    const incomingMessage: ChatMsgHistoryItem = {
      authorId: sAgentId,
      content,
      createdAt: DateTime.now(),
      messageId: currentMessageIdRef.current || crypto.randomUUID(),
      role: ChatRoleEnum.ASSISTANT,
      updatedAt: DateTime.now()
    };


    setSChatMsgHistory( prevHistory => {
      const updatedHistory = [ ...prevHistory ];
      const existingMessageIndex = prevHistory.findLastIndex( msg => msg.messageId === incomingMessage.messageId );

      if( existingMessageIndex === -1 ) {
        // novel message - add new message
        updatedHistory.push( incomingMessage );
      } else {
        // Subsequent chunks - update existing message
        updatedHistory[existingMessageIndex] = incomingMessage;
      }

      return updatedHistory;
    } );



    setTimeout( () => {
      messagesEndRef.current?.scrollIntoView( { behavior: 'smooth' } );
    }, 100 );
  }, [ sAgentId ] );

  const { processStream, abort } = useStreamingParser( {
    parseChunk: parseAgentChunk,
    onUpdateMessage: handleUpdateAgentMessage,
    onError: ( error ) => {
      console.error( error.message );
      toast.error( error.message );
      loadLogs();
    },
    onAbort: () => {
      setSIsWaiting( false );
      if ( correlationIdRef.current ) {
        loadLogs();
      }
    },
    onComplete: () => {
      loadLogs();
    }
  } );

  const handleStop = useCallback( () => {
    if( abortControllerRef.current ) {
      abortControllerRef.current.abort();
    }
    abort();
  }, [ abort ] );

  const loadLogs = useCallback( async() => {
    const cid = correlationIdRef.current;
    const [ actionResult, modelResult, requestResult ] = await Promise.allSettled( [
      APIGateway.logs.actionLogsGet( { agent_id: sAgentId, correlation_id: cid } ),
      APIGateway.logs.modelLogsGet( { agent_id: sAgentId, correlation_id: cid } ),
      APIGateway.logs.requestLogsGet( { agent_id: sAgentId, correlation_id: cid } )
    ] );

    const logs: PlaygroundLog[] = [
      ...( actionResult.status === 'fulfilled' ? actionResult.value.agent_actions.map( l => ( { ...l, _logType: 'action' as const } ) ) : [] ),
      ...( modelResult.status === 'fulfilled' ? modelResult.value.model_invocations.map( l => ( { ...l, _logType: 'model' as const } ) ) : [] ),
      ...( requestResult.status === 'fulfilled' ? requestResult.value.completion_requests.map( l => ( { ...l, _logType: 'request' as const } ) ) : [] )
    ];

    const sortedArray = logs.sort( ( a, b ) => new Date( a.start_time ).getTime() - new Date( b.start_time ).getTime()
    );
    setSLogs( sortedArray );
  }, [ sAgentId ] );

  const onClickChat = useCallback( async( mode: string ) => {
    setSIsWaiting( true );
    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();

    try {
      let compReqMsg: ChatMsgHistoryItem<ChatMessageContentTextual> | ChatMsgHistoryItem<ChatMessageContentStructured> | undefined = undefined;
      if( mode == 'text' ) {
        compReqMsg = {
          authorId: sessionContext!.userId!,
          content: {
            type: ChatMsgTypeEnum.TEXTUAL_CONTENT,
            text: inputRef.current
          },
          createdAt: DateTime.now(),
          messageId: crypto.randomUUID(),
          role: ChatRoleEnum.USER,
          updatedAt: DateTime.now()
        };
      }
      if ( mode == 'structured' ) {
        try {
          const data = JSON.parse( inputRef.current );
          compReqMsg = {
            authorId: sessionContext!.userId!,
            content: {
              type: ChatMsgTypeEnum.STRUCTURED_CONTENT,
              data: data
            },
            createdAt: DateTime.now(),
            messageId: crypto.randomUUID(),
            role: ChatRoleEnum.USER,
            updatedAt: DateTime.now()
          };
        } catch {
          toast.error( 'Input is not valid JSON' );
          setSIsWaiting( false );
          abortControllerRef.current = null;
          return;
        }
      }
      if ( typeof( compReqMsg ) == 'undefined' ) {
        toast.error( 'Invalid content type' );
        setSIsWaiting( false );
        abortControllerRef.current = null;
        return;
      }

      const newChatMsgHistory: ChatMsgHistory = [
        ...sChatMsgHistory,
        compReqMsg
      ];

      setSChatMsgHistory( newChatMsgHistory );

      setTimeout( ()=>{
        messagesEndRef.current?.scrollIntoView( { behavior: 'smooth' } );
      }, 250 );

      setSInput( '' );

      const typeCompatibleMessages = newChatMsgHistory.filter( msg => {
        return [ 'TextualContent', 'StructuredContent' ].includes( msg.content.type );
      } );

      const args: AgentInvokeArgs = {
        clientId: 'aidp-studio',
        context: {
          values: sContext ? JSON.parse( sContext ) : []
        },
        debug: sDebug,
        messages: typeCompatibleMessages,
        agentId: sAgentId!,
        users: sChatUsers!
      };

      if( sCorrelationId ) {
        args.correlationId = sCorrelationId;
      }

      const stream = await APIGateway.agents.agentInvokeAidpStream( args, abortControllerRef.current.signal );
      await processStream( stream );
    } catch( e ) {
      const error = e as Error;
      // Don't show error toast if the request was aborted by user
      if( error.name !== 'AbortError' ) {
        console.error( error.message );
        toast.error( error.message );
      }
    }

    setSIsWaiting( false );
    abortControllerRef.current = null;
  }, [ sessionContext, sChatMsgHistory, sContext, sDebug, sAgentId, sChatUsers, sCorrelationId, processStream ] );

  const onClickReset = useCallback( () => { setSActiveLog( undefined ); setSLogs( [] ); correlationIdRef.current = undefined; setSCorrelationId( undefined ); setSChatMsgHistory( [] ); }, [] );

  // Auto-submit the pre-filled replay input once chat users are wired up AND
  // the async pre-fill fetch has finished. Runs at most once per page load.
  useEffect( () => {
    if ( replayHandledRef.current ) return;
    if ( !replayReady ) return;
    if ( sIsLoading || !sChatUsers ) return;
    if ( searchParams.get( 'autoSend' ) !== '1' ) return;
    if ( !inputRef.current ) return;
    replayHandledRef.current = true;
    onClickChat( replayModeRef.current );
  }, [ replayReady, sIsLoading, sChatUsers, onClickChat, searchParams ] );

  const handleInputChange = useCallback( ( value: string ) => {
    setSInput( value );
  }, [] );

  const handleContextChange = useCallback( ( value: string ) => {
    setSContext( value );
  }, [] );

  const handleInputsChange = useCallback( ( value: string ) => {
    setSInputs( value );
  }, [] );

  const onLogClick = ( index: number ) =>{
    if( sLogs && sLogs[index] ) setSActiveLog( sLogs[index] );
  };
  const onCloseLogView = () => {
    setSActiveLog( undefined );
  };

  const memoizedMessages = useMemo( () => sChatMsgHistory.map( ( msg: ChatMsgHistoryItem ): ChatConversationMessage => {
    if ( msg.content.type === ChatMsgTypeEnum.TEXTUAL_CONTENT ) {
      return { role: msg.role, text: msg.content.text as unknown as string };
    } else {
      return { role: msg.role, json: msg.content as unknown as string };
    }
  } ), [ sChatMsgHistory ] );

  return (
    <div className={ clsx( 'page', 'no_overflow' ) }>
      <ToastContainer />
      {
        sIsLoading
          ? <Loader />
          : (
              <div className={ clsx( 'layout__col', 'overflow_hide', styles.parent_container ) }>
                <div className={ clsx( 'layout__row', styles.playground_type ) }>
                  <PlaygroundType label="Full-Code Agent" />
                </div>
                <div className={ clsx( 'layout__row', styles.playground_info ) }>
                  <PlaygroundInfo
                    name={ sAgentId }
                    onReset={ onClickReset }
                    backHref="/agents"
                  />
                </div>
                <div className={ clsx( 'layout__row', 'overflow_hide', styles.playground_chat ) }>
                  <div className={ clsx( 'layout__col', styles.chat_context ) } data-testid="playground-context-panel">
                    <div data-testid="playground-inputs-section">
                      <AgentVariables
                        label="Inputs"
                        value={ sInputs }
                        onChange={ handleInputsChange }
                      />
                    </div>
                    <div data-testid="playground-context-section">
                      <AgentVariables
                        label="Context"
                        value={ sContext }
                        onChange={ handleContextChange }
                      />
                    </div>
                    <RenderLogList
                      logs = { sLogs }
                      onLogClick = { onLogClick }
                    />
                  </div>
                  <div className={ clsx( 'layout__col', 'overflow_hide', styles.chat_convo ) }>
                    {sActiveLog && (
                      <div className={ styles.logView }>
                        <h2>Log Details <a onClick={ onCloseLogView } className={ styles.closebtn }><IoMdClose /></a></h2>

                        <LogDetail
                          log= { sActiveLog }
                        />
                      </div>
                    )}
                    <div className={ clsx( 'layout__col', 'overflow_hide', styles.chat_container ) }>

                      <PlaygroundChat
                        id={ id }
                        input={ sInput }
                        isWaiting={ sIsWaiting }
                        messages={ memoizedMessages }
                        enableStructured= { true }
                        messagesEndRef={ messagesEndRef }
                        onClickChat={ onClickChat }
                        onClickStop={ handleStop }
                        onInputChange={ handleInputChange }
                      />
                    </div>
                  </div>
                </div>
              </div>
            )
      }
    </div>
  );
}
